// A21 — «Kontent katalogi» (10 §A21). Module-local data layer. Does NOT touch
// `lib/adapters.ts` / `i18n/*` (hot files). Registration = ./REGISTER.md.
//
// REAL (wired to shipped endpoints):
//   • Stiker moderatsiya/trending → GET /admin/stickers/sets (+ moderate) [stickers.go]
//   • Reaksiyalar → GET /admin/analytics/reactions (top_emojis) [admin.go]
//   • Media saqlash → GET /admin/analytics/media (files/file_variants) [admin.go]
// GREENFIELD (fail-soft empty until backend ships — same as Ads/SEO/Developers):
//   • copyright / authors / emoji-sets / emoji-status / emoji-mod / featured /
//     miniapps / categories / search-ranking / catalog-reports
// SKELETON (data pipeline / pure-ML infra kutadi — screenStatus skeleton:true):
//   • TGS validatsiya (A21.06) · Tavsiya algoritmi (A21.14) · Dublikat media (A21.17)
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

async function safeObj<T>(path: string, signal: AbortSignal): Promise<T | null> {
  try {
    return await api.get<T>(path, { signal })
  } catch {
    return null
  }
}

// ── REAL: stiker to'plamlari (sticker_sets — stickers.go AdminSets) ────────────
/** A21.02/A21.03 — admin sticker set (matches services.AdminStickerSet). */
export interface CatStickerSetRow {
  id: number
  owner_id: number
  owner_name: string
  title: string
  short_name: string
  status: string // pending|active|rejected
  sticker_count: number
  installs: number
  animated: boolean
  reject_reason?: string
}

/** A21.02 — moderation queue: pending sets first. */
export function fetchStickerModeration(params: FetchParams): Promise<FetchResult<CatStickerSetRow>> {
  return safeList<CatStickerSetRow>('/admin/stickers/sets?status=pending', 'sets', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.title} ${r.short_name} ${r.owner_name}`),
  )
}

/** A21.03 — trending: active sets ranked by installs (most-installed first). */
export function fetchTrendingStickers(params: FetchParams): Promise<FetchResult<CatStickerSetRow>> {
  return safeList<CatStickerSetRow>('/admin/stickers/sets?status=active', 'sets', params.signal).then(
    (rows) => {
      const ranked = [...rows].sort((a, b) => b.installs - a.installs)
      return toResult(ranked, params, (r) => `${r.title} ${r.short_name} ${r.owner_name}`)
    },
  )
}

/** POST /admin/stickers/sets/:id/moderate {approve, reason} (A21.02). */
export function moderateStickerSet(id: number, approve: boolean, reason: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/stickers/sets/${id}/moderate`, { approve, reason })
}

// ── REAL: reaksiyalar (message_reactions — admin.go ReactionsOverview) ─────────
interface ReactionsOverview {
  reactions_total: number
  distinct_emojis: number
  reactors: number
  reacted_messages: number
  top_emojis: { emoji: string; count: number }[]
}
/** A21.09 — a reaction emoji + how often it is used across all chats. */
export interface ReactionRow {
  id: number
  emoji: string
  count: number
}
export async function fetchReactions(params: FetchParams): Promise<FetchResult<ReactionRow>> {
  const o = await safeObj<ReactionsOverview>('/admin/analytics/reactions', params.signal)
  const rows: ReactionRow[] = (o?.top_emojis ?? []).map((e, i) => ({ id: i + 1, emoji: e.emoji, count: e.count }))
  return toResult(rows, params, (r) => r.emoji)
}

// ── REAL: media saqlash (files/file_variants — admin.go MediaOverview) ─────────
interface MediaOverview {
  files_total: number
  files_ready: number
  files_failed: number
  owners: number
  total_bytes: number
  variants_total: number
  variant_bytes: number
  by_kind: { key: string; count: number }[]
}
/** A21.16 — one storage metric row (scalar totals + per-kind breakdown). */
export interface MediaStatRow {
  id: number
  metric: string
  value: number
  note: string
}
const fmtBytes = (n: number): string => {
  if (!n) return '0 B'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)))
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`
}
export async function fetchMediaStats(params: FetchParams): Promise<FetchResult<MediaStatRow>> {
  const o = await safeObj<MediaOverview>('/admin/analytics/media', params.signal)
  const rows: MediaStatRow[] = []
  if (o) {
    let id = 0
    const push = (metric: string, value: number, note = '') => rows.push({ id: ++id, metric, value, note })
    push(CAT_STR.mFilesTotal, o.files_total)
    push(CAT_STR.mFilesReady, o.files_ready)
    push(CAT_STR.mFilesFailed, o.files_failed)
    push(CAT_STR.mOwners, o.owners)
    push(CAT_STR.mTotalBytes, o.total_bytes, fmtBytes(o.total_bytes))
    push(CAT_STR.mVariants, o.variants_total)
    push(CAT_STR.mVariantBytes, o.variant_bytes, fmtBytes(o.variant_bytes))
    for (const k of o.by_kind ?? []) push(`${CAT_STR.mKind}: ${k.key}`, k.count)
  }
  return toResult(rows, params, (r) => r.metric)
}

// ── GREENFIELD (fail-soft empty until backend ships) ──────────────────────────
/** A21.04 — copyright / DMCA complaint against a sticker or emoji set. */
export interface CopyrightComplaintRow {
  id: number
  set_id: number
  set_title: string
  complainant: string
  reason: string
  status: string // open|resolved|rejected
  created_at: string
}
export function fetchCopyrightComplaints(params: FetchParams): Promise<FetchResult<CopyrightComplaintRow>> {
  return safeList<CopyrightComplaintRow>('/admin/stickers/copyright-complaints', 'complaints', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.set_title} ${r.complainant}`),
  )
}

/** A21.05 — a sticker-set author (creator) with aggregate counts. */
export interface StickerAuthorRow {
  id: number
  name: string
  sets_count: number
  installs_total: number
  status: string // active|blocked
}
export function fetchStickerAuthors(params: FetchParams): Promise<FetchResult<StickerAuthorRow>> {
  return safeList<StickerAuthorRow>('/admin/stickers/authors', 'authors', params.signal).then((rows) =>
    toResult(rows, params, (r) => r.name),
  )
}

/** A21.06 — TGS (animated sticker) validation result (SKELETON — TGS-lint infra). */
export interface TgsValidationRow {
  id: number
  set_title: string
  file_id: string
  result: string // valid|invalid|pending
  error: string
  checked_at: string
}
export function fetchTgsValidations(params: FetchParams): Promise<FetchResult<TgsValidationRow>> {
  return safeList<TgsValidationRow>('/admin/stickers/tgs-validation', 'entries', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.set_title} ${r.file_id}`),
  )
}

/** A21.07 — a custom-emoji set. */
export interface EmojiSetRow {
  id: number
  title: string
  short_name: string
  owner_name: string
  emoji_count: number
  status: string // pending|active|rejected
}
export function fetchEmojiSets(params: FetchParams): Promise<FetchResult<EmojiSetRow>> {
  return safeList<EmojiSetRow>('/admin/emoji/sets', 'sets', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.short_name} ${r.owner_name}`),
  )
}

/** A21.08 — an emoji status (profile status badge) available in the catalog. */
export interface EmojiStatusRow {
  id: number
  emoji: string
  title: string
  is_premium: boolean
  uses: number
  status: string // active|hidden
}
export function fetchEmojiStatuses(params: FetchParams): Promise<FetchResult<EmojiStatusRow>> {
  return safeList<EmojiStatusRow>('/admin/emoji/statuses', 'statuses', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.emoji} ${r.title}`),
  )
}

/** A21.10 — a custom-emoji set awaiting moderation. */
export interface EmojiModerationRow {
  id: number
  title: string
  owner_name: string
  emoji_count: number
  status: string // pending|active|rejected
  reason: string
}
export function fetchEmojiModeration(params: FetchParams): Promise<FetchResult<EmojiModerationRow>> {
  return safeList<EmojiModerationRow>('/admin/emoji/moderation', 'entries', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.owner_name}`),
  )
}

/** A21.11 — a featured (hand-curated) catalog entry. */
export interface FeaturedEntryRow {
  id: number
  entity_type: string // sticker|emoji|miniapp
  title: string
  position: number
  active: boolean
  curated_by: string
}
export function fetchFeatured(params: FetchParams): Promise<FetchResult<FeaturedEntryRow>> {
  return safeList<FeaturedEntryRow>('/admin/catalog/featured', 'entries', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.entity_type}`),
  )
}

/** A21.12 — a Mini App listed in the catalog. */
export interface MiniAppRow {
  id: number
  name: string
  short_name: string
  owner: string
  category: string
  status: string // active|pending|blocked
  opens: number
}
export function fetchMiniApps(params: FetchParams): Promise<FetchResult<MiniAppRow>> {
  return safeList<MiniAppRow>('/admin/catalog/miniapps', 'apps', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.short_name} ${r.owner}`),
  )
}

/** A21.13 — a catalog category. */
export interface CatalogCategoryRow {
  id: number
  name: string
  slug: string
  kind: string // sticker|emoji|miniapp
  items_count: number
  sort_order: number
}
export function fetchCategories(params: FetchParams): Promise<FetchResult<CatalogCategoryRow>> {
  return safeList<CatalogCategoryRow>('/admin/catalog/categories', 'categories', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.slug} ${r.kind}`),
  )
}

/** A21.14 — a recommendation-algorithm tuning parameter (SKELETON — pure-ML infra). */
export interface RecommendationParamRow {
  id: number
  name: string
  value: string
  weight: number
  enabled: boolean
}
export function fetchRecommendationParams(params: FetchParams): Promise<FetchResult<RecommendationParamRow>> {
  return safeList<RecommendationParamRow>('/admin/catalog/recommendation', 'params', params.signal).then((rows) =>
    toResult(rows, params, (r) => r.name),
  )
}

/** A21.15 — a search-ranking boost rule for the catalog. */
export interface SearchRankingRow {
  id: number
  term: string
  entity_type: string
  boost: number
  impressions: number
  clicks: number
}
export function fetchSearchRanking(params: FetchParams): Promise<FetchResult<SearchRankingRow>> {
  return safeList<SearchRankingRow>('/admin/catalog/search-ranking', 'rules', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.term} ${r.entity_type}`),
  )
}

/** A21.17 — a detected duplicate media pair (SKELETON — perceptual-hash pure-ML). */
export interface DuplicateMediaRow {
  id: number
  file_id: string
  dup_of: string
  kind: string
  similarity: number
  status: string // pending|merged|ignored
}
export function fetchDuplicateMedia(params: FetchParams): Promise<FetchResult<DuplicateMediaRow>> {
  return safeList<DuplicateMediaRow>('/admin/media/duplicates', 'pairs', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.file_id} ${r.dup_of} ${r.kind}`),
  )
}

/** A21.18 — a generated catalog report row (period aggregate). */
export interface CatalogReportRow {
  id: number
  period: string
  entity_type: string
  new_items: number
  installs: number
  removals: number
  generated_at: string
}
export function fetchCatalogReports(params: FetchParams): Promise<FetchResult<CatalogReportRow>> {
  return safeList<CatalogReportRow>('/admin/catalog/reports', 'reports', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.period} ${r.entity_type}`),
  )
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER.md). */
export const CAT_STR = {
  // common
  title: 'Название',
  shortName: 'Краткое имя',
  owner: 'Владелец',
  status: 'Статус',
  reason: 'Причина',
  created: 'Создан',
  pending: 'На модерации',
  active: 'Активен',
  rejected: 'Отклонён',
  blocked: 'Заблокирован',
  approve: 'Одобрить',
  reject: 'Отклонить',
  done: 'Готово',
  failed: 'Не удалось выполнить',
  rejectPrompt: 'Укажите причину отклонения',
  emptyQueue: 'Очередь пуста',
  // A21.02 moderation / A21.03 trending
  stickerModeration: 'Стикеры — модерация',
  trendingStickers: 'Трендовые стикеры',
  count: 'Стикеров',
  installs: 'Установок',
  animated: 'Анимированный',
  // A21.04 copyright
  copyright: 'Жалобы на авторские права',
  complainant: 'Заявитель',
  setTitle: 'Набор',
  // A21.05 authors
  authors: 'Авторы стикеров',
  name: 'Имя',
  setsCount: 'Наборов',
  installsTotal: 'Всего установок',
  // A21.06 TGS
  tgs: 'Валидация TGS (анимация)',
  fileId: 'file_id',
  result: 'Результат',
  error: 'Ошибка',
  checkedAt: 'Проверен',
  // A21.07 emoji sets
  emojiSets: 'Наборы кастомных эмодзи',
  emojiCount: 'Эмодзи',
  // A21.08 emoji status
  emojiStatuses: 'Эмодзи-статусы',
  emoji: 'Эмодзи',
  premium: 'Премиум',
  uses: 'Использований',
  // A21.09 reactions
  reactions: 'Управление реакциями',
  usageCount: 'Использований',
  // A21.10 emoji moderation
  emojiModeration: 'Модерация эмодзи',
  // A21.11 featured
  featured: 'Избранный каталог',
  entityType: 'Тип',
  position: 'Позиция',
  activeFlag: 'Показывается',
  curatedBy: 'Куратор',
  // A21.12 mini apps
  miniApps: 'Каталог Mini App',
  category: 'Категория',
  opens: 'Открытий',
  // A21.13 categories
  categories: 'Категории каталога',
  slug: 'Слаг',
  kind: 'Вид',
  items: 'Элементов',
  sortOrder: 'Порядок',
  // A21.14 recommendation
  recommendation: 'Настройки алгоритма рекомендаций',
  paramName: 'Параметр',
  value: 'Значение',
  weight: 'Вес',
  enabled: 'Включён',
  // A21.15 search ranking
  searchRanking: 'Рейтинг поиска',
  term: 'Запрос',
  boost: 'Boost',
  impressions: 'Показы',
  clicks: 'Клики',
  // A21.16 media stats
  mediaStats: 'Статистика медиахранилища',
  metric: 'Метрика',
  mValue: 'Значение',
  note: 'Примечание',
  mFilesTotal: 'Всего файлов',
  mFilesReady: 'Готовых файлов',
  mFilesFailed: 'Ошибок обработки',
  mOwners: 'Владельцев',
  mTotalBytes: 'Объём файлов (байт)',
  mVariants: 'Вариантов',
  mVariantBytes: 'Объём вариантов (байт)',
  mKind: 'Тип',
  // A21.17 duplicate media
  duplicates: 'Поиск дубликатов медиа',
  dupOf: 'Дубликат для',
  similarity: 'Схожесть',
  // A21.06 TGS validatsiya qoidalari (JONLI CRUD)
  tgsDesc: 'Правила валидации TGS (Lottie/gzip) — админ управляет набором',
  tgsRuleName: 'Правило',
  tgsParam: 'Параметр',
  tgsValue: 'Значение',
  tgsAction: 'Действие',
  addTgsRule: 'Добавить правило',
  // A21.14 tavsiya algoritmi (JONLI config, catalog_reco_*)
  recoDesc: 'Веса алгоритма рекомендаций каталога (app_settings)',
  recoSection: 'Веса ранжирования',
  recoEnabled: 'Рекомендации включены',
  recoInstalls: 'Вес установок',
  recoRecency: 'Вес свежести',
  recoTrending: 'Вес трендов',
  recoPersonalization: 'Вес персонализации',
  recoMaxResults: 'Макс. результатов',
  // A21.17 perceptual-dedup (JONLI CRUD, media_dedup_hashes)
  dedupDesc: 'Индекс перцептивных хэшей (pHash) для поиска дубликатов',
  dedupPhash: 'pHash',
  dedupMediaId: 'media_id',
  addDedup: 'Добавить хэш',
  // A21.18 catalog reports
  reports: 'Отчёты каталога',
  period: 'Период',
  newItems: 'Новых',
  removals: 'Удалено',
  generatedAt: 'Сформирован',
} as const
